// bd-deepcontext-b4zw — everything the "View Runs" modal needs: the selected job,
// its runs, the SSE stream while runs are live, and the per-run unmatched-URL
// drill-down. Extracted from ScheduledJobs.tsx (handleShowRuns / handleCancelRun
// / handleToggleDrilldown / handleChangeUnmatchedSort / closeRunsModal).
import { useCallback, useEffect, useState } from "react";
import type { ScheduledJobRun } from "../jobRuns/types";
import { scheduledJobsApi } from "./api";
import { extractApiErrorMessage } from "./apiErrors";
import { streamJobRuns, type RunsStreamHandle } from "./runsStream";
import type { ScheduledJob, UnmatchedSort, UnmatchedUrl } from "./types";

export interface JobRunsModalState {
  showRunsModal: boolean;
  selectedJob: ScheduledJob | null;
  jobRuns: ScheduledJobRun[];
  isLive: boolean;
  expandedRunId: string | null;
  unmatchedSort: UnmatchedSort;
  unmatchedUrls: UnmatchedUrl[];
  unmatchedLoading: boolean;
  unmatchedError: string | null;
  openRunsModal: (job: ScheduledJob) => Promise<void>;
  closeRunsModal: () => void;
  cancelRun: (jobId: string, runId: string) => Promise<void>;
  toggleDrilldown: (jobId: string, runId: string) => Promise<void>;
  changeUnmatchedSort: (
    jobId: string,
    runId: string,
    sort: UnmatchedSort,
  ) => Promise<void>;
}

export function useJobRunsModal({
  setLoading,
  setError,
  setSuccess,
}: {
  setLoading: (loading: boolean) => void;
  setError: (message: string | null) => void;
  setSuccess: (message: string | null) => void;
}): JobRunsModalState {
  const [showRunsModal, setShowRunsModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState<ScheduledJob | null>(null);
  const [jobRuns, setJobRuns] = useState<ScheduledJobRun[]>([]);
  const [eventSource, setEventSource] = useState<RunsStreamHandle | null>(null);
  // Unmatched-URL drill-down (per run) state
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [unmatchedSort, setUnmatchedSort] = useState<UnmatchedSort>("lowest");
  const [unmatchedUrls, setUnmatchedUrls] = useState<UnmatchedUrl[]>([]);
  const [unmatchedLoading, setUnmatchedLoading] = useState(false);
  const [unmatchedError, setUnmatchedError] = useState<string | null>(null);

  // Cleanup event source on unmount
  useEffect(() => {
    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [eventSource]);

  const openRunsModal = useCallback(
    async (job: ScheduledJob) => {
      try {
        setLoading(true);
        setSelectedJob(job);

        // Initial load
        const runs = await scheduledJobsApi.getJobRuns(job.id);
        setJobRuns(runs);
        setShowRunsModal(true);

        // Clear any existing event source
        if (eventSource) {
          eventSource.close();
          setEventSource(null);
        }

        // Set up SSE streaming for running jobs
        const hasRunningJobs = runs.some(
          (run) => run.status === "running" || run.status === "pending",
        );
        if (hasRunningJobs) {
          setEventSource(streamJobRuns(job.id, setJobRuns));
        }
      } catch (err: any) {
        console.error("Failed to load job runs:", err);
        if (err.response?.status === 403) {
          setError("Admin access required to view job runs");
        } else {
          setError("Failed to load job runs");
        }
      } finally {
        setLoading(false);
      }
    },
    [eventSource, setError, setLoading],
  );

  const closeRunsModal = useCallback(() => {
    setShowRunsModal(false);
    setSelectedJob(null);
    setJobRuns([]);
    setExpandedRunId(null);
    setUnmatchedUrls([]);
    setUnmatchedError(null);
    // Clear event source
    if (eventSource) {
      eventSource.close();
      setEventSource(null);
    }
  }, [eventSource]);

  const cancelRun = useCallback(
    async (jobId: string, runId: string) => {
      if (
        !window.confirm(
          "Are you sure you want to cancel this job run? Partial results will be preserved.",
        )
      ) {
        return;
      }

      try {
        setLoading(true);
        setError(null);

        await scheduledJobsApi.cancelJobRun(jobId, runId);
        setSuccess("Job run cancelled successfully");

        // Refresh the job runs
        if (selectedJob) {
          const runs = await scheduledJobsApi.getJobRuns(selectedJob.id);
          setJobRuns(runs);
        }
      } catch (err: any) {
        console.error("Failed to cancel job run:", err);
        if (err.response?.status === 403) {
          setError("Admin access required to cancel job runs");
        } else if (err.response?.status === 404) {
          setError("Job run not found or not in a cancellable state");
        } else {
          setError(extractApiErrorMessage(err, "Failed to cancel job run"));
        }
      } finally {
        setLoading(false);
      }
    },
    [selectedJob, setError, setLoading, setSuccess],
  );

  const fetchUnmatchedUrls = useCallback(
    async (jobId: string, runId: string, sort: UnmatchedSort) => {
      setUnmatchedLoading(true);
      setUnmatchedError(null);
      try {
        const data = await scheduledJobsApi.getRunUnmatchedUrls(
          jobId,
          runId,
          sort,
        );
        setUnmatchedUrls(data.urls);
      } catch (err: any) {
        setUnmatchedUrls([]);
        setUnmatchedError(
          extractApiErrorMessage(err, "Failed to load unmatched URLs"),
        );
      } finally {
        setUnmatchedLoading(false);
      }
    },
    [],
  );

  const toggleDrilldown = useCallback(
    async (jobId: string, runId: string) => {
      if (expandedRunId === runId) {
        // Collapse
        setExpandedRunId(null);
        setUnmatchedUrls([]);
        setUnmatchedError(null);
        return;
      }
      // Expand: default to "lowest" (Borderline)
      setExpandedRunId(runId);
      setUnmatchedSort("lowest");
      await fetchUnmatchedUrls(jobId, runId, "lowest");
    },
    [expandedRunId, fetchUnmatchedUrls],
  );

  const changeUnmatchedSort = useCallback(
    async (jobId: string, runId: string, sort: UnmatchedSort) => {
      if (sort === unmatchedSort) return;
      setUnmatchedSort(sort);
      await fetchUnmatchedUrls(jobId, runId, sort);
    },
    [fetchUnmatchedUrls, unmatchedSort],
  );

  return {
    showRunsModal,
    selectedJob,
    jobRuns,
    isLive:
      !!eventSource && eventSource.readyState === EventSource.OPEN,
    expandedRunId,
    unmatchedSort,
    unmatchedUrls,
    unmatchedLoading,
    unmatchedError,
    openRunsModal,
    closeRunsModal,
    cancelRun,
    toggleDrilldown,
    changeUnmatchedSort,
  };
}
